import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, Search, ChevronLeft, ChevronRight,
  Lock, LockOpen, CheckCircle2, Send,
} from 'lucide-react';

import { usePermission }   from '@/hooks/usePermission';
import { Permissions }     from '@/constants/permissions';
import { AppRoutes }       from '@/constants/routes';
import { Spinner }         from '@/components/ui/Spinner';
import { cn }              from '@/utils/cn';
import { periodClosingApi } from '@/api/periodClosing.api';

type Action = 'soft' | 'hard' | 'lock' | 'reopen' | 'submit' | 'approve';

function extractError(e: unknown): string {
  const err = e as { response?: { data?: { message?: string; errors?: { message: string }[] } } };
  return err.response?.data?.errors?.[0]?.message
      ?? err.response?.data?.message
      ?? 'That action could not be completed.';
}

// â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
/**
 * The shape /period-closing actually returns.
 *
 * This screen was written against `/finance/period-close`, a path that does not exist, reading
 * `periodCode`, `periodName`, `closedBy` and `closedDate` — none of which the real endpoint sends.
 * The list call 404'd into a console.error, so the table was empty on every visit, and each action
 * button posted to a second route that does not exist either. Nothing on the screen has ever
 * worked. The names below are the server's.
 */
interface PeriodCloseListDto {
  id: string;
  financialYearCode: string;
  financialPeriodCode: string;
  module: string;
  status: string;            // Open | SoftClosed | HardClosed | Locked
  closeType?: string | null; // Soft | Hard | Locked
  closingDate?: string | null;
  workflowStatus: string;    // None | Submitted | Approved | Rejected
  createdDate: string;
}

interface PeriodCloseFilter {
  page: number;
  pageSize: number;
  companyId?: string;
  financialPeriodCode?: string;
  module?: string;
  status?: string;
}

const STATUS_COLORS: Record<string, string> = {
  Open:       'bg-green-100 dark:bg-green-900/30  text-green-700 dark:text-green-400',
  SoftClosed: 'bg-amber-100 dark:bg-amber-900/30  text-amber-700 dark:text-amber-400',
  HardClosed: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
  Locked:     'bg-red-100 dark:bg-red-900/30    text-red-700 dark:text-red-400',
  Submitted:  'bg-blue-100 dark:bg-blue-900/30   text-blue-700 dark:text-blue-400',
  Approved:   'bg-teal-100 dark:bg-teal-900/30   text-teal-700 dark:text-teal-400',
};

const MODULES = ['GL', 'AR', 'AP', 'BNK', 'FA', 'INV', 'PAY'];
const STATUSES = ['Open', 'SoftClosed', 'HardClosed', 'Locked', 'Submitted', 'Approved'];

// â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
export const PeriodClosingListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();

  const canCreate  = hasPermission(Permissions.PERIOD_CLOSE_VIEW);
  const canSoft    = hasPermission(Permissions.PERIOD_CLOSE_SOFT);
  const canHard    = hasPermission(Permissions.PERIOD_CLOSE_HARD);
  const canLock    = hasPermission(Permissions.PERIOD_CLOSE_LOCK);
  const canReopen  = hasPermission(Permissions.PERIOD_CLOSE_REOPEN);
  const canSubmit  = hasPermission(Permissions.PERIOD_CLOSE_SUBMIT);
  const canApprove = hasPermission(Permissions.PERIOD_CLOSE_APPROVE);

  const [items,   setItems]   = useState<PeriodCloseListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [filter, setFilter] = useState<PeriodCloseFilter>({ page: 1, pageSize });
  const [search, setSearch] = useState('');
  const [error,  setError]  = useState<string | null>(null);
  const [reopenFor, setReopenFor] = useState<{ id: string; period: string; reason: string } | null>(null);

  const load = useCallback(async (f: PeriodCloseFilter) => {
    setLoading(true);
    try {
      const res  = await periodClosingApi.getList({
        page: f.page, pageSize: f.pageSize,
        financialPeriodCode: f.financialPeriodCode, module: f.module, status: f.status,
      });
      const data = res.data?.data;
      setItems((data?.items ?? []) as unknown as PeriodCloseListDto[]);
      setTotal(data?.totalCount ?? 0);
      setError(null);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  const handleSearch = () => {
    setFilter(f => ({ ...f, financialPeriodCode: search || undefined, page: 1 }));
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilter(f => ({ ...f, page: newPage }));
  };

  /**
   * Closing is one endpoint taking the kind of close; submit and approve are the workflow either
   * side of it. Failures used to go to console.error, so a refusal looked identical to nothing
   * happening — they are shown now.
   */
  const doAction = async (id: string, action: Action) => {
    try {
      setError(null);
      if (action === 'soft')    await periodClosingApi.close(id, { closeType: 'Soft',   remarks: null });
      if (action === 'hard')    await periodClosingApi.close(id, { closeType: 'Hard',   remarks: null });
      if (action === 'lock')    await periodClosingApi.close(id, { closeType: 'Locked', remarks: null });
      if (action === 'submit')  await periodClosingApi.submit(id);
      if (action === 'approve') await periodClosingApi.approve(id);
      if (action === 'reopen') {
        const reason = reopenFor?.reason?.trim();
        if (!reason) { setError('Give a reason for reopening the period.'); return; }
        await periodClosingApi.reopen(id, { reason });
        setReopenFor(null);
      }
      load(filter);
    } catch (err) {
      setError(extractError(err));
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">

      {/* â”€â”€ Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Lock size={20} className="text-teal-600 dark:text-teal-400" />
              Period Closing
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {total.toLocaleString()} entries
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => load(filter)}
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.PERIOD_CLOSING_LIST + '/new')}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Entry
              </button>
            )}
          </div>
        </div>

        {/* â”€â”€ Filters â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="flex items-center gap-3 mt-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search period code..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>

          <select
            value={filter.module ?? ''}
            onChange={e => { setFilter(f => ({ ...f, module: e.target.value || undefined, page: 1 })); setPage(1); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2
                       bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
          >
            <option value="">All Modules</option>
            {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>

          <select
            value={filter.status ?? ''}
            onChange={e => { setFilter(f => ({ ...f, status: e.target.value || undefined, page: 1 })); setPage(1); }}
            className="text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-2
                       bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-teal-500 outline-none"
          >
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Reopening asks why. The server requires a reason and the record keeps it. */}
      {reopenFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl dark:bg-slate-800">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              Reopen {reopenFor.period}
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Reopening a closed period is audited. Say why.
            </p>
            <textarea
              autoFocus rows={3} value={reopenFor.reason}
              onChange={e => setReopenFor(r => (r ? { ...r, reason: e.target.value } : r))}
              placeholder="Reason for reopening"
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-teal-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setReopenFor(null)}
                className="rounded-lg px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700">
                Cancel
              </button>
              <button onClick={() => doAction(reopenFor.id, 'reopen')}
                className="rounded-lg bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700">
                Reopen period
              </button>
            </div>
          </div>
        </div>
      )}

      {/* â”€â”€ Table â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Spinner size="lg" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Lock size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No period closing entries found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Period</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Module</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Close Type</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Approval</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Closed Date</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map(item => (
                <tr key={item.id} className="hover:bg-teal-50 dark:hover:bg-slate-700/50 transition-colors group">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-teal-700 dark:text-teal-400">{item.financialPeriodCode}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{item.financialYearCode}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700 dark:text-slate-300">{item.module}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{item.closeType ?? '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', STATUS_COLORS[item.status] ?? 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-gray-400')}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">{item.workflowStatus}</td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400 text-xs">
                    {item.closingDate ? new Date(item.closingDate).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canSoft && item.status === 'Open' && item.workflowStatus === 'Approved' && (
                        <button onClick={() => doAction(item.id, 'soft')}
                          title="Soft Close"
                          className="px-2 py-1 text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 rounded transition-colors">
                          Soft Close
                        </button>
                      )}
                      {canHard && item.status === 'SoftClosed' && (
                        <button onClick={() => doAction(item.id, 'hard')}
                          title="Hard Close"
                          className="px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 hover:bg-orange-200 rounded transition-colors">
                          Hard Close
                        </button>
                      )}
                      {canLock && item.status === 'HardClosed' && (
                        <button onClick={() => doAction(item.id, 'lock')}
                          title="Lock"
                          className="px-2 py-1 text-xs bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 rounded transition-colors flex items-center gap-0.5">
                          <Lock size={11} /> Lock
                        </button>
                      )}
                      {canReopen && ['SoftClosed', 'HardClosed'].includes(item.status) && (
                        <button onClick={() => setReopenFor({ id: item.id, period: item.financialPeriodCode, reason: '' })}
                          title="Reopen"
                          className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 rounded transition-colors flex items-center gap-0.5">
                          <LockOpen size={11} /> Reopen
                        </button>
                      )}
                      {canSubmit && item.workflowStatus === 'None' && (
                        <button onClick={() => doAction(item.id, 'submit')}
                          title="Submit for Approval"
                          className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-200 rounded transition-colors flex items-center gap-0.5">
                          <Send size={11} /> Submit
                        </button>
                      )}
                      {canApprove && item.workflowStatus === 'Submitted' && (
                        <button onClick={() => doAction(item.id, 'approve')}
                          title="Approve"
                          className="px-2 py-1 text-xs bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 hover:bg-teal-200 rounded transition-colors flex items-center gap-0.5">
                          <CheckCircle2 size={11} /> Approve
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* â”€â”€ Pagination â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-3">
          <p className="text-sm text-slate-500 dark:text-gray-400">
            Page {page} of {totalPages} · {total.toLocaleString()} records
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const n = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
              return (
                <button key={n} onClick={() => handlePageChange(n)}
                  className={cn('w-8 h-8 text-sm rounded transition-colors',
                    n === page ? 'bg-teal-600 text-white font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300'
                  )}>{n}</button>
              );
            })}
            <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

